import React, { useEffect, useState } from "react";
import { useAuthContext } from "../../../context/auth";
import { postClassRanking } from "../../../client-api/fits";
import { ClassRankingEntry } from "../../../types";
import BottomNav from "../BottomNav";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import cn from "../../../utils/cn";
import Lottie from "react-lottie";
import confettiAnimation from "../ClubDay/confetti.json";

const ASSET = "/heart-rate/ranking";

// 실시간 심박 화면(WorkoutCoinBoard)과 같은 존 색.
const ZONE_COLOR: Record<string, string> = {
  Z1: "text-[#A56BD7]",
  Z2: "text-[#3CB1DB]",
  Z3: "text-[#58C613]",
  Z4: "text-[#FF990A]",
  Z5: "text-[#E5484D]",
};

/** 순위 메달. 1~3위는 금·은·동, 4·5위는 작은 회색(Figma 5948:29975 · 30251 · 30254 · 30333). */
function Medal({ rank }: { rank: number }) {
  if (rank >= 4) {
    return (
      <div className="relative size-[80px] mb-[-20px] z-[2] overflow-clip shrink-0">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-[67.556px] rounded-full flex items-center justify-center"
          style={{ backgroundImage: "linear-gradient(180deg, #C8D1DC 0%, #8D9AAD 100%)" }}
        >
          <p className="text-[39.111px] leading-[1.4] font-['TheJamsil6ExtraBold'] font-extrabold text-[#51565D] opacity-90">
            {rank}
          </p>
        </div>
      </div>
    );
  }
  const style =
    rank === 1
      ? { bg: "linear-gradient(180deg, #FBE7A9 27.404%, #D4A12E 100%)", text: "text-[#674310]" }
      : rank === 2
        ? { bg: "linear-gradient(180deg, #E9EFF3 16.827%, #6F7D8B 100%)", text: "text-[#3E454D]" }
        : { bg: "linear-gradient(180deg, #EEB793 34.135%, #A76341 100%)", text: "text-[#532819]" };
  return (
    <div className="relative size-[80px] mb-[-20px] z-[2] shrink-0">
      {rank === 1 && (
        <>
          {/* 1위 리본(Figma 5948:29977·29978) */}
          <div className="absolute left-[-0.89px] top-[56.44px] w-[36.301px] h-[40.973px] flex items-center justify-center">
            <img
              src={`${ASSET}/ribbon-left.svg`}
              alt=""
              className="w-[21.902px] h-[34.667px] -rotate-[150deg]"
            />
          </div>
          <div className="absolute left-[45.41px] top-[56.44px] w-[36.301px] h-[40.973px] flex items-center justify-center">
            <img
              src={`${ASSET}/ribbon-right.svg`}
              alt=""
              className="w-[21.902px] h-[34.667px] -rotate-[30deg] -scale-y-100"
            />
          </div>
        </>
      )}
      <div
        className="absolute inset-0 rounded-full flex items-center justify-center p-[8.889px]"
        style={{ backgroundImage: style.bg }}
      >
        <p className={cn("text-[44.444px] leading-[1.4] font-['TheJamsil6ExtraBold'] font-extrabold opacity-90", style.text)}>
          {rank}
        </p>
      </div>
    </div>
  );
}

function StatRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex gap-[6px] items-center">
        {icon}
        <p className="text-[20px] font-medium text-white whitespace-nowrap">{label}</p>
      </div>
      {children}
    </div>
  );
}

function ValueUnit({ value, unit }: { value: string; unit: string }) {
  return (
    <div className="flex gap-[2px] items-end">
      <p className="text-[28px] font-bold text-white whitespace-nowrap">{value}</p>
      <p className="text-[18px] font-semibold text-white whitespace-nowrap pb-[3px]">{unit}</p>
    </div>
  );
}

function RankCard({ entry }: { entry: ClassRankingEntry }) {
  const first = entry.rank === 1;
  const minutes = Math.floor(entry.targetZoneSeconds / 60);
  const seconds = entry.targetZoneSeconds % 60;
  return (
    <div className="flex flex-col items-center isolate shrink-0">
      <Medal rank={entry.rank} />
      <div
        className={cn(
          "relative z-[1] w-[344px] rounded-[16px] flex flex-col gap-[32px] items-center pt-[48px] pb-[42px] px-[30px]",
          first ? "border-[3px] border-solid border-[#FBE7A9]" : "bg-[rgba(255,255,255,0.12)]",
        )}
        style={
          first
            ? {
                backgroundImage:
                  "linear-gradient(180deg, rgba(251, 231, 169, 0.12) 27.404%, rgba(212, 161, 46, 0.12) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%)",
              }
            : undefined
        }
      >
        <div className="flex gap-[24px] items-center max-w-full">
          {entry.profileImageUrl ? (
            <img
              src={entry.profileImageUrl}
              alt=""
              className="size-[91px] rounded-full object-cover shrink-0"
            />
          ) : (
            <img src={`${ASSET}/avatar.svg`} alt="" className="size-[91px] shrink-0" />
          )}
          <div className="flex flex-col gap-[6px] items-start min-w-0">
            <p className="text-[42px] font-semibold text-white truncate max-w-[170px]">
              {entry.name ?? ""}
            </p>
            {entry.topZone && (
              <p className={cn("text-[24px] font-semibold", ZONE_COLOR[entry.topZone])}>
                {entry.topZone}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-[22px] items-start w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-[4px] items-center">
              <img src={`${ASSET}/trophy.svg`} alt="" className="size-[36px]" />
              <p className="text-[28px] font-semibold text-white whitespace-nowrap">운동점수</p>
            </div>
            <div className="flex gap-[6px] items-end">
              <p className="text-[40px] font-bold text-[#7DE9FF] whitespace-nowrap">{entry.score}</p>
              <p className="text-[34px] font-bold text-[#7DE9FF] py-[2px]">점</p>
            </div>
          </div>
          <div className="flex flex-col gap-[18px] items-start pl-[2px] w-full">
            <StatRow icon={<img src={`${ASSET}/heart.svg`} alt="" className="size-[32px]" />} label="평균심박">
              <ValueUnit value={entry.avgBpm != null ? String(entry.avgBpm) : "-"} unit="BPM" />
            </StatRow>
            <StatRow icon={<img src={`${ASSET}/fire.svg`} alt="" className="size-[32px]" />} label="소모 칼로리">
              <ValueUnit
                value={entry.calories != null ? String(Math.round(entry.calories)) : "-"}
                unit="Kcal"
              />
            </StatRow>
            <StatRow icon={<img src={`${ASSET}/coin.svg`} alt="" className="size-[32px]" />} label="획득 포인트">
              <p className="text-[28px] font-bold text-white whitespace-nowrap">+{entry.earnedCoin}P</p>
            </StatRow>
            <StatRow icon={<img src={`${ASSET}/timer.svg`} alt="" className="size-[32px]" />} label="목표존 유지">
              <div className="flex gap-[4px] items-end">
                <div className="flex gap-[2px] items-end">
                  <p className="text-[22px] font-bold text-white">{minutes}</p>
                  <p className="text-[18px] font-semibold text-white py-[2px]">분</p>
                  <p className="text-[22px] font-bold text-white">{seconds}</p>
                  <p className="text-[18px] font-semibold text-white py-[2px]">초</p>
                </div>
                {entry.targetZonePercent != null && (
                  <p className="text-[18px] font-semibold text-white py-[2px]">
                    ({entry.targetZonePercent}%)
                  </p>
                )}
              </div>
            </StatRow>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 그룹 수업 '오늘의 TOP 5'(Figma 5946:28469). 쿨다운 다음, 클럽데이 앞에 나온다.
 *
 * 화면이 뜨면 서버에 그 수업을 지금 확정해 달라고 하고(코인도 이때 지급) 순위를 받는다.
 * 다음 화면(클럽데이)은 아래 이전·다음 바로 넘긴다(다른 수업 화면과 같음).
 * 컨트롤(did 0)과 디스플레이(did 1)가 함께 불러도 서버가 한 번만 확정한다.
 * 순위: 운동 점수 > 목표존 유지 > 평균 심박 > 소모 칼로리(Figma 주석).
 */
function ClassRanking() {
  const { classId, did } = useAuthContext();
  const [ranking, setRanking] = useState<ClassRankingEntry[]>([]);

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (classId === undefined || !username) return;
    let cancelled = false;
    postClassRanking(classId, username)
      .then((data) => {
        if (!cancelled) setRanking(data);
      })
      .catch((e) => console.log("[ClassRanking]", e));
    return () => {
      cancelled = true;
    };
  }, [classId]);

  // BottomNav 의 빈 칸(flex-1)보다 크게 늘어나 남는 높이를 이 영역이 모두 쓴다(WorkoutType1 과 같음).
  // 수정 시안(5946:28469): 위 단계 탭(컨트롤 화면만, LandscapeProgramLayout)과 아래 이전·다음 바
  // (BottomNav)는 다른 수업 화면과 같다. 그 사이 영역에 제목·카드·배너를 놓는다.
  //   좌표는 시안(탭 58px 아래 기준): 제목 40 · 카드 220 · 배너 770.
  return (
    <LandscapeProgramLayout step={4}>
      {/* 순위가 뜨면 클럽데이 뱃지 획득과 같은 폭죽을 뒤에 깐다. */}
      {ranking.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-10">
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: confettiAnimation,
              rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
            }}
            height="100%"
            width="100%"
            isClickToPauseDisabled
          />
        </div>
      )}
      <div
        className={cn(
          "relative z-20 w-[1920px] grow-[100] basis-0 min-h-0 font-['Freesentation'] leading-[normal]",
          // 회원용은 하단 바가 빠져 늘어난 높이만큼 내용을 아래로 내려 가운데 맞춘다.
          did === "1" && "translate-y-[66px]",
        )}
      >
        <div className="absolute left-[562px] top-[40px] w-[796px] flex flex-col gap-[6px] items-center text-center">
          <div className="flex gap-[24px] items-center text-[60px] font-['TheJamsil6ExtraBold'] font-extrabold uppercase leading-[1.2] whitespace-nowrap">
            <p className="text-white">오늘의</p>
            <p
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(158deg, #E3FAFF 0%, #53CAF5 100%)" }}
            >
              TOP 5
            </p>
          </div>
          <p
            className="text-[34px] font-semibold opacity-[0.86] bg-clip-text text-transparent whitespace-pre-wrap"
            style={{ backgroundImage: "linear-gradient(177deg, #E3FAFF 0%, #D4F4FF 100%)" }}
          >
            {"끝까지 집중해주셔서  대단합니다! 올바른 운동, 함께 잘 해냈어요!"}
          </p>
        </div>

        <div
          className={cn(
            "absolute left-0 w-[1920px] h-[520px] px-[60px] flex gap-[20px] items-end justify-center",
            did === "1" ? "top-[280px]" : "top-[220px]",
          )}
        >
          {ranking.map((entry) => (
            <RankCard key={entry.userId} entry={entry} />
          ))}
        </div>

        <div
          className={cn(
            "absolute left-[60px] w-[1800px] rounded-[16px] px-[10px] py-[16px] flex flex-col items-center justify-center",
            did === "1" ? "top-[830px]" : "top-[770px]",
          )}
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(125, 233, 255, 0) 0%, rgba(125, 233, 255, 0.16) 30%, rgba(125, 233, 255, 0.16) 70%, rgba(125, 233, 255, 0) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.06) 100%)",
          }}
        >
          <div className="flex gap-[60px] items-center">
            <img src={`${ASSET}/logo.png`} alt="" className="w-[172px] h-[61.117px] object-contain shrink-0" />
            <div className="flex gap-[30px] items-start text-[32px] whitespace-nowrap">
              <div className="flex gap-[16px] items-center">
                <p className="font-semibold text-white">내 기록은</p>
                <div className="flex gap-[4px] items-center">
                  <p className="font-bold text-[#7DE9FF]">앱</p>
                  <p className="font-semibold text-white">에서 확인해보세요!</p>
                </div>
              </div>
              <p className="font-medium text-[#C2E5EC]">나의 운동 기록과 상세 분석을 볼 수 있어요.</p>
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default ClassRanking;
